import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useLocalStorage } from 'usehooks-ts';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  GeojsonFeatureCollection,
  LocationMapParamsInputObject,
  Site,
  useSectorsSearchListLazyQuery,
  useSiteDestroyMutation,
} from 'graphql-common';
import usePrevious from '@lib/hooks/usePrevious';
import { Value, Values } from '@lib/interfaces/form';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import useSitesQueryHook from 'utils/fetch/hooks/useSitesQueryHook';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { SITE_LIST_FILTER_FORM } from '@lib/enums/localStorageKeys';
import { SITE_LIST_FILTER_EMPTY_VALUES } from 'routes/Sites/utils/useSiteListFilterFields';
import { ListQueryHookResult } from 'utils/fetch/types';
import { LoadOptions, SelectOption } from '@lib/components/Select/types';
import { GetUrlParams } from '@lib/enums/urls';
import SitesFilterFieldNames from '@lib/enums/fieldNames/sitesFilterFieldNames';
import { APP_URLS } from 'constants/urls';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { SiteListView } from 'routes/Sites/enums';
import useSiteLocationsQueryHook from 'utils/fetch/hooks/useSiteLocationsQueryHook';
import useSiteLocationCenterQueryHook, {
  UseSectorQueryHookResult,
} from 'utils/fetch/hooks/useSiteLocationCenterQueryHook';

export interface UseSiteListActionsResult {
  clearFilter: () => void;
  closeFilter: () => void;
  filterParams: { [p: string]: unknown };
  isEmptyState: boolean;
  isEmptyStateByFilter: boolean;
  isEmptyStateVisible: boolean;
  isPaginationVisible: boolean;
  isViewOnly: boolean;
  onConfirmedDelete: () => void;
  onDeleteHandler: (v?: Site) => void;
  onEditHandler: (v?: Site) => void;
  onFilterSubmit: (values: Values) => void;
  onUpdateFilterParams: (values: Values) => void;
  onViewHandler: (v?: Site) => void;
  openFilter: () => void;
  resetAllFilters: () => void;
  resetFilterParams: () => void;
  resetSearchQuery: () => void;
  sectorsLoadOptions: LoadOptions;
  siteLocationsQueryHookResult: ListQueryHookResult<GeojsonFeatureCollection>;
  sitesQueryHookResult: ListQueryHookResult<Site>;
  siteLocationCenterQueryHookResult: UseSectorQueryHookResult;
}

export default function useSiteListActions({
  useModalManagementResult,
  useBulkSelectResult,
  view,
  mapParams,
}: {
  useModalManagementResult: UseModalManagementResult;
  useBulkSelectResult: UseBulkSelectResult<Site>;
  view: SiteListView;
  mapParams: LocationMapParamsInputObject | null;
}): UseSiteListActionsResult {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModalManagementResult;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(SITE_LIST_FILTER_FORM, SITE_LIST_FILTER_EMPTY_VALUES);
  const [paginationParams, setPaginationParams] = usePaginationParams();
  const prevView = usePrevious(view);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) ===
    AccessLevelEnum.Read;

  // Sites
  const sectorIds = (
    _get(filterParams, SitesFilterFieldNames.Sectors, []) as SelectOption[]
  ).map(({ value }) => value);
  const additionalSiteFilters = sectorIds?.length
    ? [
        {
          sectorId: {
            grouping: FilterGroupingEnum.Or,
            predicate: { in: sectorIds },
          },
        },
      ]
    : undefined;
  const sitesQueryHookResult = useSitesQueryHook({
    paginationParams,
    additionalFilters: additionalSiteFilters,
    searchQuery,
    skip: view === SiteListView.Map,
  });
  const {
    collection,
    firstLoading: sitesFirstLoading,
    totalCount,
  } = sitesQueryHookResult;

  const shouldFetchSiteLocationCenter =
    !mapParams || !Object.keys(mapParams)?.length || !_get(mapParams, 'center');
  const siteLocationCenterQueryHookResult = useSiteLocationCenterQueryHook({
    defaultFilters: [
      {
        kind: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: 'point' },
        },
      },
    ],
    additionalSiteFilters,
    searchQuery,
    skip: !shouldFetchSiteLocationCenter,
  });

  const shouldFetchSiteLocations =
    view === SiteListView.Map &&
    !shouldFetchSiteLocationCenter &&
    !siteLocationCenterQueryHookResult.firstLoading &&
    mapParams;
  const siteLocationsQueryHookResult = useSiteLocationsQueryHook({
    additionalSiteFilters,
    searchQuery,
    skip: !shouldFetchSiteLocations,
    mapParams,
  });

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const sectorsLoadOptions = getSectorsLoadOptionsFn(
    fetchSectorsSearchListLazyQuery,
  );

  // Mutations
  const [destroyMutation] = useSiteDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.SiteDestroyModal);
      sitesQueryHookResult.refetch();
      navigate(APP_URLS.dashboard.sites.index.getPathWithQuery());
    },
    onError: (error) => showGraphQLErrors(error, t),
  });

  // Actions
  const onEditHandler = (v?: Site) => {
    if (v?.id) {
      navigate(
        APP_URLS.dashboard.sites.edit.getDynamicPath({
          pathParts: { id: v.id },
        }),
      );
    }
  };
  const onViewHandler = (v?: Site) => {
    if (v?.id) {
      navigate(
        APP_URLS.dashboard.sites.view.getDynamicPath({
          pathParts: { id: v.id },
        }),
      );
    }
  };
  const onDeleteHandler = (v?: Site) => {
    if (v) {
      useBulkSelectResult.handleSelectSingleTableItem(v);
      openModal(Modals.SiteDestroyModal);
    }
  };
  const onConfirmedDelete = () => {
    closeModal(Modals.SiteDestroyModal);
    const id = useBulkSelectResult.selectedSingleTableItem?.id;
    if (id) {
      toast({
        content: t('destroy-site-success'),
        closeCallback: () => {
          if (id) {
            destroyMutation({
              variables: { id },
            });
          }
        },
      });
    }
  };
  const resetSearchQuery = () => setSearchQuery('');
  const resetFilterParams = () => setFilterParams({});
  const resetAllFilters = () => {
    setSearchQuery('');
    setFilterParams({});
  };
  const openFilter = () => openModal(Modals.SiteListFilter);
  const closeFilter = () => closeModal(Modals.SiteListFilter);
  const clearFilter = () => {
    closeModal(Modals.SiteListFilter);
    resetFilterParams();
  };
  const onUpdateFilterParams = (values: Values) => {
    setFilterParams(values);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  };
  const onFilterSubmit = (values: Values) => {
    onUpdateFilterParams(values);
    closeFilter();
  };

  const isContentVisible =
    view === SiteListView.Map ? true : !!totalCount && !!collection.length;
  const isEmptyStateVisible =
    view === SiteListView.Map ? false : !isContentVisible && !sitesFirstLoading;
  const isPaginationVisible =
    !!totalCount && !!collection.length && totalCount > collection.length;
  const isFilterApplied = sectorIds.length > 0;
  const isEmptyState = isEmptyStateVisible && !searchQuery && !isFilterApplied;
  const isEmptyStateByFilter =
    isEmptyStateVisible && (!!searchQuery || isFilterApplied);

  useEffect(() => {
    if (prevView !== view) {
      if (view === SiteListView.Map) {
        siteLocationsQueryHookResult.refetch();
      } else {
        sitesQueryHookResult.refetch();
      }
    }
  }, [prevView, siteLocationsQueryHookResult, sitesQueryHookResult, view]);

  return {
    clearFilter,
    closeFilter,
    filterParams,
    isEmptyState,
    isEmptyStateByFilter,
    isEmptyStateVisible,
    isPaginationVisible,
    isViewOnly,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onFilterSubmit,
    onUpdateFilterParams,
    onViewHandler,
    openFilter,
    resetAllFilters,
    resetFilterParams,
    resetSearchQuery,
    sectorsLoadOptions,
    siteLocationsQueryHookResult,
    sitesQueryHookResult,
    siteLocationCenterQueryHookResult,
  };
}
